// Store app (static demo pages/Mobile.jsx, screen 34) on real data: three
// working phones. Alarm: the worst open alarm with its last two hours,
// acknowledge and the first steps. Install: the next sensor in stock, installed
// with the same dialog as the device page. Closing check: the store's points
// read by the sensors, the tasks and a probe, signed as a check record
// (kind=check, like Quality > Checks). Measuring-point spots and radio
// metadata are not in the platform yet, so the install phone leaves them out.
import { useState } from "preact/hooks";
import { Link } from "wouter-preact";
import { can, opsApi } from "@gimasi/giothub-shell";
import Icon from "../components/Icon.jsx";
import { InstallDialog } from "../components/InstallDialog";
import { useT, type T } from "../i18n";
import { useLoad } from "../lib/load";
import { ALARM_ST, fleet, useFleet } from "../data/fleet";
import { REGIMES, clsName } from "../data/classes";
import { sites, type LocNode, type Point } from "../data/locations";
import { addRecord, records, type Check } from "../data/quality";
import type { Install } from "../data/installs";
import { State } from "./common";
import { series as readSeries } from "../lib/series";

const ORDER: Record<string, number> = { crit: 0, alarm: 1, warn: 2, delayed: 3, ok: 4 };
const sev = (type: string) => ALARM_ST[type] || "warn";

function Phone({ title, tab, t, children }: { title: string; tab: string; t: T; children: preact.ComponentChildren }) {
  return <div class="phone"><div class="notch" /><div class="pscreen">{children}</div><nav class="ptabs">{["alarms", "checks", "scan", "store"].map((k) => <span key={k} class={tab === k ? "on" : ""}><Icon name={k === "alarms" ? "bell" : k === "checks" ? "check" : k === "scan" ? "plan" : "locations"} size={18} />{t("mob.tab." + k)}</span>)}</nav><span class="sr">{title}</span></div>;
}

async function last2h(id: string) {
  const to = new Date(), from = new Date(to.getTime() - 2 * 3600e3);
  for (const key of ["ext_temperature", "temperature_1"]) {
    try { const s = await readSeries(id, { key, from: from.toISOString(), to: to.toISOString(), resolution: "raw" }); if (s.items.length) return s.items.map((x) => x.value); } catch { /* key not on this sensor */ }
  }
  return [];
}

export default function Mobile() {
  const t = useT();
  const f = useFleet(t.lang);
  if (!f) return <div class="page"><State loading /></div>;
  const vis = sites(f.root);
  const siteOf = new Map<string, LocNode>();
  vis.forEach((s) => s.points.forEach((p) => siteOf.set(p.id, s)));
  const top = [...f.alarms].filter((a) => sev(a.type) !== "delayed").sort((a, b) => ORDER[sev(a.type)] - ORDER[sev(b.type)] || Date.parse(b.utc) - Date.parse(a.utc))[0];
  const stock = f.points.filter((p) => !p.labels.site).sort((a, b) => a.name.localeCompare(b.name));
  const store = (top && siteOf.get(top.device_id)) || [...vis].sort((a, b) => b.points.length - a.points.length)[0];
  return (
    <div class="page">
      <div class="page-h"><div><h1>{t("nav.mobile")}</h1><div class="sub">{t("mob.sub")}</div></div></div>
      <div class="phones">
        <AlarmPhone t={t} a={top} p={top ? f.byId.get(top.device_id) : undefined} site={top ? siteOf.get(top.device_id) : undefined} />
        <InstallPhone t={t} p={stock[0]} last={f.installs.find((x) => x.device_id && !x.body.stock)} />
        <ClosingPhone t={t} site={store} />
      </div>
    </div>
  );
}

function AlarmPhone({ t, a, p, site }: { t: T; a?: (typeof fleet.value & {})["alarms"][number]; p?: Point; site?: LocNode }) {
  const [acked, setAcked] = useState(false);
  const [steps, setSteps] = useState([false, false, false]);
  const curve = useLoad(() => (p ? last2h(p.id) : Promise.resolve([])), [p?.id]);
  if (!a) return <Phone title={t("mob.alarm")} tab="alarms" t={t}><div class="phead"><b>{t("mob.alarm")}</b></div><div class="pbody"><div class="pcard ok"><b>{t("dash.allClear")}</b></div></div></Phone>;
  const s = sev(a.type);
  const cls = p?.regime ? REGIMES[p.regime] : undefined;
  const limit = cls ? (a.type === "TEMP_LOW" ? cls.low : s === "crit" ? cls.crit : s === "alarm" ? cls.alarm : cls.warn) : null;
  const pts = curve.data || [];
  const lo = Math.min(...pts, limit ?? Infinity) - 1, hi = Math.max(...pts, limit ?? -Infinity) + 1;
  const y = (v: number) => 70 - ((v - lo) / (hi - lo || 1)) * 64;
  const P = pts.map((v, i) => `${((i / Math.max(1, pts.length - 1)) * 300).toFixed(1)},${y(v).toFixed(1)}`).join(" ");
  const mins = Math.max(0, Math.round((Date.now() - Date.parse(a.utc)) / 60e3));
  const done = acked || a.state !== "open";
  const ack = async () => { try { await opsApi.ackAlarm(a.id, t("mob.ack")); setAcked(true); } catch { /* stays open */ } };
  return (
    <Phone title={t("mob.alarm")} tab="alarms" t={t}>
      <div class="palarm" style={s === "crit" ? undefined : { background: s === "alarm" ? "var(--alarm)" : "var(--warn)" }}>
        <div class="row" style="justify-content:space-between"><span class="lbl" style="color:#FFD9D4">{s === "crit" ? t("mob.critical") : t("st." + s)}</span><span class="mono">{t.dur(mins)}</span></div>
        <b style="font-size:20px">{p?.name || a.device_serial}</b>
        <div class="row" style="align-items:baseline;gap:8px"><span class="mono" style="font-size:40px;font-weight:600">{t.temp(p?.temp ?? null)}</span>{limit != null && <span style="font-size:12px">{t("x.mob.limit", { v: t.temp(limit) })}</span>}</div>
        <small>{[p?.labels.zone, site?.name].filter(Boolean).join(" · ")}</small>
      </div>
      <div class="pbody">
        {pts.length > 1 && <svg viewBox="0 0 300 76" width="100%" style="background:var(--surface);border:1px solid var(--line);border-radius:10px">{limit != null && <rect x="0" y="0" width="300" height={Math.max(0, y(limit))} fill="var(--crit)" opacity=".1" />}<polyline points={P} fill="none" stroke="var(--brand)" stroke-width="2" /></svg>}
        <Link class="pai" href={"/alarms/" + a.id}><Icon name="ai" size={14} /> {t("x.mob.ai")}</Link>
        <button class="btn pri pfull" type="button" disabled={done || !can("alarms:acknowledge")} onClick={ack}>{done ? t("mob.acked") : t("mob.ack")}</button>
        <span class="lbl">{t("mob.steps")}</span>
        {["s1", "s2", "s3"].map((k, i) => <label key={k} class="pcheck"><input type="checkbox" checked={steps[i]} onChange={() => setSteps(steps.map((x, j) => (j === i ? !x : x)))} />{t("x.mob." + k)}</label>)}
      </div>
    </Phone>
  );
}

function InstallPhone({ t, p, last }: { t: T; p?: Point; last?: Install }) {
  const [open, setOpen] = useState(false);
  const [done, setDone] = useState(false);
  return (
    <Phone title={t("mob.install")} tab="scan" t={t}>
      <div class="phead"><b>{t("mob.install")}</b><span class="sub2">{p ? t("x.mob.fromStock") : ""}</span></div>
      <div class="pbody">
        {p ? <>
          <div class="pcard"><span class="lbl">{t("mob.scanned")}</span><div class="row" style="justify-content:space-between"><b>VST · {String(p.labels.use_case || "")}</b><span class={"st st-" + (p.st === "delayed" ? "delayed" : "ok")}>{p.st === "delayed" ? t("st.delayed") : "online"}</span></div><span class="mono sub2">{p.serial}</span></div>
          <div class="pcard ok"><span class="lbl">{t("x.mob.lastReading")}</span><span class="mono" style="font-size:28px;font-weight:600">{t.temp(p.temp)}</span><span class="sub2">{p.lastContact ? t("x.mob.via", { t: t.time(new Date(p.lastContact)) }) : t("x.never")}</span></div>
          <button class="btn pri pfull" type="button" disabled={done || !can("devices:transfer")} onClick={() => setOpen(true)}>{done ? t("x.mob.installed") : t("x.mob.install")}</button>
        </> : <div class="pcard"><span class="lbl">{t("x.mob.noStock")}</span></div>}
        {last && <div class="pcard"><span class="lbl">{t("x.mob.lastInstall")}</span><b>{last.body.cabinet || last.device_serial}</b><span class="sub2">{[fleet.value?.stores.find((x) => x.value === last.body.site)?.name || last.body.site, t.date(new Date(last.occurred_at))].filter(Boolean).join(" · ")}</span></div>}
      </div>
      {open && p && <InstallDialog p={p} mode="install" onClose={(ok) => { setOpen(false); if (ok) setDone(true); }} />}
    </Phone>
  );
}

function ClosingPhone({ t, site }: { t: T; site?: LocNode }) {
  const [tasks, setTasks] = useState([false, false]);
  const [probe, setProbe] = useState("");
  const [signed, setSigned] = useState(false);
  const [err, setErr] = useState("");
  const day0 = new Date(); day0.setHours(0, 0, 0, 0);
  const today = useLoad(() => (site ? records<Check>("check", site.value, day0.getTime(), Date.now() + 60e3) : Promise.resolve([])), [site?.value, signed]);
  if (!site) return <Phone title={t("mob.closing")} tab="checks" t={t}><div class="phead"><b>{t("mob.closing")}</b></div><div class="pbody"><div class="pcard"><span class="lbl">{t("x.mob.noStore")}</span></div></div></Phone>;
  const done = (k: Check["kind"]) => (today.data || []).filter((r) => r.body.kind === k).sort((a, b) => Date.parse(b.occurred_at) - Date.parse(a.occurred_at))[0];
  const chip = (k: Check["kind"], label: string) => { const r = done(k); return <div class={"pchip " + (r ? (r.body.ok ? "ok" : "warn") : "warn")}>{label}<br /><b>{r ? t.time(new Date(r.occurred_at)) : "—"}</b></div>; };
  const pts = [...site.points].sort((a, b) => ORDER[a.st] - ORDER[b.st] || a.name.localeCompare(b.name));
  const allOk = pts.every((p) => p.st === "ok");
  const sign = async () => {
    setErr("");
    try { await addRecord("check", site.value, t("x.chk.closing"), { kind: "closing", ok: allOk && tasks.every(Boolean), probe: probe || undefined, note: tasks.map((x, i) => `${t("x.mob.t" + (i + 1))}: ${x ? "✓" : "✗"}`).join("; ") } satisfies Check, allOk ? "ok" : "not_ok"); setSigned(true); }
    catch (e) { setErr((e as Error).message); }
  };
  return (
    <Phone title={t("mob.closing")} tab="checks" t={t}>
      <div class="phead"><span class="sub2">{site.name}</span><b>{t("mob.closing")}</b></div>
      <div class="pbody">
        <div class="pgrid three">{chip("opening", t("mob.opening"))}{chip("goods", t("mob.goods"))}{chip("closing", t("mob.closingS"))}</div>
        <span class="lbl">{t("mob.bySensors")}</span>
        {pts.map((p) => <div key={p.id} class="prow"><span><b>{p.name}</b><br /><small class="sub2">{t.temp(p.temp)}{p.regime ? " · " + clsName(t.lang, p.regime) : ""}</small></span><span class={"st st-" + (p.st === "ok" ? "ok" : p.st === "delayed" ? "delayed" : "warn")}>{p.st === "ok" ? "OK" : p.st === "delayed" ? t("st.delayed") : t("mob.check")}</span></div>)}
        <span class="lbl">{t("mob.tasks")}</span>
        {["t1", "t2"].map((k, i) => <label key={k} class="pcheck"><input type="checkbox" checked={tasks[i]} onChange={() => setTasks(tasks.map((x, j) => (j === i ? !x : x)))} />{t("x.mob." + k)}</label>)}
        <label class="pcheck" style="justify-content:space-between">{t("x.mob.probe")}<input class="pin" value={probe} placeholder="°C" onInput={(e) => setProbe((e.target as HTMLInputElement).value)} /></label>
        {err && <small style="color:var(--crit)">{err}</small>}
        <button class="btn pri pfull" type="button" disabled={signed || !tasks.every(Boolean) || !can("records:write")} onClick={sign}>{signed ? t("mob.signed") : t("mob.sign")}</button>
      </div>
    </Phone>
  );
}
